import posthog from 'posthog-js';
import { watch } from 'vue';
import { useUserStore } from '@/modules/profile/stores/user';
import { sessionReplayConfig } from './config';

let isInitialized = false;

const logDebug = (...args: unknown[]) =>
  console.log('%cSessionReplay', 'background:#673ab7;color:#fff;padding:3px 6px;border-radius:3px;font-weight:bold', ...args);

const logError = (...args: unknown[]) =>
  console.error('%cSessionReplay', 'background:#c0392b;color:#fff;padding:3px 6px;border-radius:3px;font-weight:bold', ...args);

export const initializeSessionReplay = async (): Promise<void> => {
  if (isInitialized) {
    return;
  }

  try {
    /* eslint-disable camelcase -- snake_case keys are mandated by the posthog-js API */
    posthog.init(sessionReplayConfig.apiKey(), {
      api_host: sessionReplayConfig.host,
      autocapture: sessionReplayConfig.autocapture,
      rageclick: sessionReplayConfig.rageclick,
      // SPA: pageviews are captured manually via the router (see capturePageview).
      capture_pageview: false,
      // Cookies are unreliable inside a WebView; use localStorage only.
      persistence: 'localStorage',
      session_recording: {
        maskAllInputs: sessionReplayConfig.maskAllInputs,
      },
    });
    /* eslint-enable camelcase */
    isInitialized = true;
    watchUserIdentity();
    logDebug('initialized', {
      host: sessionReplayConfig.host,
    });
  } catch (error) {
    logError('failed to initialize:', error);
  }
};

export const identifyReplayUser = async (id: string): Promise<void> => {
  try {
    // Anonymous: internal id only. Never pass email/name.
    posthog.identify(id);
  } catch (error) {
    logError('failed to identify:', error);
  }
};

export const resetReplay = async (): Promise<void> => {
  try {
    posthog.reset();
  } catch (error) {
    logError('failed to reset:', error);
  }
};

export const pauseReplay = async (): Promise<void> => {
  try {
    posthog.stopSessionRecording();
  } catch (error) {
    logError('failed to pause:', error);
  }
};

export const resumeReplay = async (): Promise<void> => {
  try {
    posthog.startSessionRecording();
  } catch (error) {
    logError('failed to resume:', error);
  }
};

export const capturePageview = (): void => {
  try {
    posthog.capture('$pageview');
  } catch (error) {
    logError('failed to capture pageview:', error);
  }
};

/** Single wiring point: identify on login, reset on logout. Avoids scattered calls. */
const watchUserIdentity = (): void => {
  const userStore = useUserStore();
  watch(
    () => userStore.user?.id,
    (id) => {
      if (id) {
        void identifyReplayUser(String(id));
      } else {
        void resetReplay();
      }
    },
    {
      immediate: true,
    },
  );
};
